import { isSemanticModelClass, isSemanticModelRelationship } from "../../concepts/index.js";
import { isSemanticModelClassProfile, isSemanticModelRelationshipProfile } from "../concepts/index.js";
export function isAggregatedProfiledSemanticModelClass(entity) {
    return isSemanticModelClassProfile(entity) && "conceptIris" in entity;
}
class DefaultProfileEntityAggregator {
    dependencies(entity) {
        if (isSemanticModelClassProfile(entity)) {
            return entity.profiling;
        }
        if (isSemanticModelRelationshipProfile(entity)) {
            // We need to collect from the edges.
            return entity.ends.map(item => item.profiling).flat();
        }
        return null;
    }
    aggregateSemanticModelClassProfile(profile, aggregatedProfiled) {
        const profiled = createProfiledGetter(aggregatedProfiled, profile);
        let usageNote = null;
        const usageNoteProfiled = profiled(profile.usageNoteFromProfiled);
        if (usageNoteProfiled !== undefined && isSemanticModelClassProfile(usageNoteProfiled)) {
            usageNote = usageNoteProfiled.usageNote;
        }
        else {
            usageNote = profile.usageNote;
        }
        // This collect all properties that are part of the profiled entities and merges them into the aggregated one.
        // The goal is to allow unknown properties to be aggregated.
        const otherPropertiesAggregated = {};
        const conceptIris = [];
        for (const identifier of profile.profiling) {
            const profile = profiled(identifier);
            if (isSemanticModelClass(profile) && !isSemanticModelClassProfile(profile) && profile.iri) {
                conceptIris.push(profile.iri);
            }
            else if (isAggregatedProfiledSemanticModelClass(profile)) {
                conceptIris.push(...profile.conceptIris);
            }
            else {
                // SemanticModelClassProfile should never be the case.
            }
            if (profile) {
                Object.assign(otherPropertiesAggregated, profile);
            }
        }
        return {
            // Add all properties from aggregated entities and from this one.
            ...otherPropertiesAggregated, // enforce all members to be explicitly defined
            ...profile, // enforce all members to be explicitly defined
            //
            id: profile.id,
            type: profile.type,
            profiling: profile.profiling,
            iri: profile.iri,
            externalDocumentationUrl: profile.externalDocumentationUrl,
            tags: profile.tags,
            //
            usageNote: profiled(profile.usageNoteFromProfiled)?.usageNote ?? usageNote ?? null,
            usageNoteFromProfiled: profile.usageNoteFromProfiled,
            //
            name: profiled(profile.nameFromProfiled)?.name ?? profile.name ?? null,
            nameFromProfiled: profile.nameFromProfiled,
            description: profiled(profile.descriptionFromProfiled)?.description ?? profile.description ?? null,
            descriptionFromProfiled: profile.descriptionFromProfiled,
            //
            conceptIris: conceptIris,
        };
    }
    aggregateSemanticModelRelationshipProfile(profile, aggregatedProfiled) {
        const profiled = createProfiledGetter(aggregatedProfiled, profile);
        return {
            // Add all properties from the profile.
            ...profile, // enforce all members to be explicitly defined
            //
            id: profile.id,
            type: profile.type,
            //
            ends: profile.ends.map((end, index) => ({
                // Add all properties from the profile and profiled entities.
                ...end.profiling.map(profiled).reduce((p, c) => Object.assign(p, c?.ends[index]), {}), // enforce all members to be explicitly defined
                ...end, // enforce all members to be explicitly defined
                //
                profiling: end.profiling,
                iri: end.iri,
                externalDocumentationUrl: end.externalDocumentationUrl,
                tags: end.tags,
                //
                name: profiled(end.nameFromProfiled)?.ends[index]?.name ?? end.name ?? null,
                nameFromProfiled: end.nameFromProfiled,
                description: profiled(end.descriptionFromProfiled)?.ends[index]?.description ?? end.description ?? null,
                descriptionFromProfiled: end.descriptionFromProfiled,
                usageNote: (() => {
                    // We need to do some computation.
                    const source = profiled(end.usageNoteFromProfiled);
                    if (isSemanticModelRelationshipProfile(source)) {
                        return source.ends[index]?.usageNote ?? end.usageNote;
                    }
                    else {
                        return end.usageNote;
                    }
                })(),
                usageNoteFromProfiled: end.usageNoteFromProfiled,
                concept: end.concept,
                cardinality: (() => {
                    const cardinalities = end.profiling
                        .map(identifier => profiled(identifier))
                        .map(item => item?.ends?.[index]?.cardinality)
                        .filter(item => item !== undefined && item !== null);
                    if (end.cardinality !== null) {
                        cardinalities.push(end.cardinality);
                    }
                    if (cardinalities.length === 0) {
                        // Nothing has been specified.
                        return null;
                    }
                    return cardinalityIntersection(cardinalities);
                })(),
                conceptIris: end.profiling
                    .map(identifier => profiled(identifier))
                    .map(item => {
                    if (isSemanticModelRelationship(item) && !isSemanticModelRelationshipProfile(item) && item.ends?.[index]?.iri) {
                        return item.ends[index].iri;
                    }
                    else if (isSemanticModelRelationshipProfile(item) && "conceptIris" in (item.ends?.[index] ?? {})) {
                        const end = item.ends[index];
                        return end.conceptIris;
                    }
                    else {
                        return "";
                    }
                })
                    .flat()
                    .filter(item => item && item !== ""),
            })),
        };
    }
}
function createProfiledGetter(items, defaultItem) {
    const map = {};
    items.forEach(item => map[item.id] = item);
    return (id) => map[id ?? ""] ?? defaultItem ?? null;
}
function cardinalityIntersection(cardinalities) {
    // We need to determine the intersection.
    return cardinalities.reduce((previous, current) => {
        const lower = Math.max(previous[0], current[0]);
        if (previous[1] === null && current[1] === null) {
            return [lower, null];
        }
        else if (previous[1] !== null && current[1] !== null) {
            return [lower, Math.min(previous[1], current[1])];
        }
        else if (previous[1] !== null) {
            return [lower, previous[1]];
        }
        else {
            return [lower, current[1]];
        }
    }, [0, null]);
}
export function createDefaultProfileEntityAggregator() {
    return new DefaultProfileEntityAggregator();
}
//# sourceMappingURL=aggregator.js.map